import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { ResourceType } from 'libs.library_manager';
import { Button } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { CONSENT_TEMPLATE_PREVIEW_QUERY_PARAM, consentElementsToSignatureFields } from '../bundler';
const BASE_URL = `//unpkg.com/pdfjs-dist@${pdfjs.version}`;
pdfjs.GlobalWorkerOptions.workerSrc = `${BASE_URL}/build/pdf.worker.min.mjs`;
const pdfOptions = {
    cMapUrl: `https:${BASE_URL}/cmaps/`,
    standardFontDataUrl: `https:${BASE_URL}/standard_fonts/`,
};
const PREVIEW_PAGE_SCALE = 1.25;
/**
 * Read-only PDF + percentage overlays matching the compiled consent `form.json` (same coordinates as the framework app).
 */
export const ConsentCompiledTemplatePreview = ({ pdfDataUrl, signatureFields, }) => {
    const [numPages, setNumPages] = useState(0);
    const fieldsByPage = useMemo(() => {
        const map = new Map();
        for (const f of signatureFields) {
            const p = f.page ?? 1;
            const list = map.get(p) ?? [];
            list.push(f);
            map.set(p, list);
        }
        return map;
    }, [signatureFields]);
    return (_jsx("div", { className: 'min-h-0 flex-1 overflow-auto bg-neutral-100 p-4', "data-testid": 'consent-compiled-template-preview', children: _jsx(Document, { file: pdfDataUrl, onLoadSuccess: ({ numPages: n }) => setNumPages(n), options: pdfOptions, loading: _jsx("p", { className: 'text-sm text-neutral-600', children: "Loading PDF\u2026" }), children: _jsx("div", { className: 'flex flex-col items-center gap-4', children: Array.from({ length: numPages }, (_, i) => {
                    const pageNumber = i + 1;
                    const fields = fieldsByPage.get(pageNumber) ?? [];
                    return (_jsxs("div", { className: 'relative inline-block', "data-testid": `consent-preview-page-wrap-${pageNumber}`, children: [_jsx(Page, { pageNumber: pageNumber, scale: PREVIEW_PAGE_SCALE, loading: null }), fields.map((field) => (_jsx("div", { className: 'pointer-events-none absolute z-10 box-border flex items-center justify-center border-2 border-blue-600 bg-blue-600/25 text-center', style: {
                                    left: `${field.xPct}%`,
                                    top: `${field.yPct}%`,
                                    width: `${field.widthPct}%`,
                                    height: `${field.heightPct}%`,
                                }, title: `${field.type} — ${field.role}`, children: _jsxs("span", { className: 'px-1 text-[10px] font-semibold text-white drop-shadow', children: [field.type, " (", field.role, ")"] }) }, `${pageNumber}-${field.xPct}-${field.yPct}`)))] }, pageNumber));
                }) }) }) }));
};
const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read PDF'));
    reader.readAsDataURL(blob);
});
/**
 * Full-page preview: reads `?consentPreview=<documentId>`, fetches the document from the library, and renders
 * {@link ConsentCompiledTemplatePreview}.
 */
export const ConsentTemplatePreviewPage = () => {
    const documentId = useMemo(() => new URLSearchParams(window.location.search).get(CONSENT_TEMPLATE_PREVIEW_QUERY_PARAM), []);
    const [state, setState] = useState({ status: 'loading' });
    const { get } = useApiClient(ApiClientService.LIBRARY);
    useEffect(() => {
        if (!documentId) {
            return;
        }
        (async () => {
            try {
                const { data: templateResponse } = await get(`v1/${ResourceType.CONSENT_TEMPLATE}/${documentId}`);
                const template = templateResponse.data;
                const meta = (template.metadata ?? {});
                const data = (template.data ?? {});
                const assetId = meta.consentAssetId;
                const elements = data.elements ?? [];
                const roles = data.roles ?? [];
                const templatePackageId = data.templatePackageId ?? '';
                if (!assetId) {
                    setState({ status: 'error', message: 'No CONSENT_ASSET linked to this template.' });
                    return;
                }
                const { data: assetResponse } = await get(`v1/${ResourceType.CONSENT_ASSET}/${assetId}`, { params: { withUrl: true } });
                const pdfUrl = assetResponse.data.blobUrl;
                if (!pdfUrl) {
                    setState({ status: 'error', message: 'PDF not found for this template.' });
                    return;
                }
                const blob = await fetch(pdfUrl).then((r) => r.blob());
                const pdfDataUrl = await readBlobAsDataUrl(blob);
                setState({
                    status: 'ready',
                    pdfDataUrl,
                    signatureFields: consentElementsToSignatureFields(elements, roles),
                    title: template.name,
                    templatePackageId,
                });
            }
            catch (e) {
                setState({ status: 'error', message: e instanceof Error ? e.message : 'Failed to load preview.' });
            }
        })();
    }, [documentId, get]);
    if (!documentId) {
        return (_jsxs("div", { className: 'flex min-h-screen flex-col items-center justify-center gap-4 bg-white p-6', children: [_jsxs("p", { className: 'text-neutral-700', children: ["Missing ", CONSENT_TEMPLATE_PREVIEW_QUERY_PARAM, " query parameter."] }), _jsx(Button, { variant: 'secondary', label: 'Close', onClick: () => window.close() })] }));
    }
    if (state.status === 'loading') {
        return (_jsx("div", { className: 'flex min-h-screen flex-col items-center justify-center gap-4 bg-white p-6', children: _jsx("p", { className: 'text-neutral-700', children: "Loading preview\u2026" }) }));
    }
    if (state.status === 'error') {
        return (_jsxs("div", { className: 'flex min-h-screen flex-col items-center justify-center gap-4 bg-white p-6', children: [_jsx("p", { className: 'text-neutral-700', children: state.message }), _jsx(Button, { variant: 'secondary', label: 'Close', onClick: () => window.close() })] }));
    }
    return (_jsxs("div", { className: 'flex h-screen min-h-0 flex-col bg-white', children: [_jsxs("header", { className: 'flex shrink-0 items-center justify-between gap-4 border-b border-neutral-200 px-4 py-3', children: [_jsx("h1", { className: 'truncate text-base font-semibold text-neutral-900', title: state.title, children: state.title }), _jsx("span", { className: 'shrink-0 text-xs text-neutral-500', children: state.templatePackageId }), _jsx(Button, { variant: 'transparent', size: 'sm', label: 'Close', onClick: () => window.close() })] }), _jsx(ConsentCompiledTemplatePreview, { pdfDataUrl: state.pdfDataUrl, signatureFields: state.signatureFields })] }));
};
